import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PrimaryButton, RedButton } from "../../components";
import { useWithdraws } from "../../store/useWithdraws";
import { useAlert } from "../../store/useAlert";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function toYMD(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export const WalletExportModal = ({ isOpen, onClose }: Props) => {
  const { downloadWalletReport } = useWithdraws();
  const { setAlert } = useAlert();

  const defaults = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return { from: toYMD(from), to: toYMD(to) };
  }, []);

  const [from, setFrom] = useState<string>(defaults.from);
  const [to, setTo] = useState<string>(defaults.to);
  const [isExporting, setIsExporting] = useState(false);

  // Submit gate — true if either date is missing or the range is reversed.
  const isRangeInvalid =
    !from || !to || new Date(from).getTime() > new Date(to).getTime();
  const isSubmitDisabled = isExporting || isRangeInvalid;

  if (!isOpen) {
    return null;
  }

  const handleExport = async () => {
    if (!from || !to) {
      setAlert({
        isOpen: true,
        message: "Please pick both 'From' and 'To' dates.",
        severity: "error",
      });
      return;
    }
    if (new Date(from).getTime() > new Date(to).getTime()) {
      setAlert({
        isOpen: true,
        message: "'From' must be on or before 'To'.",
        severity: "error",
      });
      return;
    }
    setIsExporting(true);
    try {
      await downloadWalletReport(from, to);
      onClose();
    } catch {
      setAlert({
        isOpen: true,
        message: "Failed to download the wallet report. Please try again.",
        severity: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return createPortal(
    <>
      <div className="opacity-25 fixed inset-0 z-[99998] bg-black" />
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-[99999] outline-none focus:outline-none">
        <div className="relative w-11/12 sm:w-2/3 lg:w-1/2 max-w-xl my-6 mx-auto">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            <div className="flex items-start justify-between p-5 border-b border-solid border-gray-200 rounded-t">
              <h3 className="text-2xl font-semibold font-satoshi text-dark">
                Download Wallet Report
              </h3>
              <button
                className="text-gray-500 text-2xl leading-none"
                onClick={() => !isExporting && onClose()}
                disabled={isExporting}
              >
                ×
              </button>
            </div>

            <div className="relative p-6 flex-auto">
              <p className="text-sm text-gray-600 mb-4">
                Exports two sections in one CSV: <b>daily</b> wallet position,
                customer payouts and customer withdrawals, and <b>monthly</b>{" "}
                store-admin withdrawals.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From
                  </label>
                  <input
                    type="date"
                    value={from}
                    max={to}
                    onChange={(e) => setFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-droppie-oranje"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To
                  </label>
                  <input
                    type="date"
                    value={to}
                    min={from}
                    max={toYMD(new Date())}
                    onChange={(e) => setTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-droppie-oranje"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end p-6 border-t border-solid border-gray-200 rounded-b gap-3">
              <RedButton
                label="Cancel"
                onClick={() => !isExporting && onClose()}
                className="h-8 flex items-center justify-center"
              />
              <PrimaryButton
                label={isExporting ? "Exporting..." : "Download CSV"}
                onClick={handleExport}
                disabled={isSubmitDisabled}
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default WalletExportModal;
